import { MessagesType } from '@telialt/common-cms/src/model';
import { Price } from '@telialt/models/src/EshopModel';
import { formatCurrencyWithoutZeroDecimals } from '@telialt/utils/src/helpers';

import { DisplayData, Prices } from './model';

export const isPriceSet = (price?: Price): price is Price => price !== undefined && price !== null;

export const formatPrice = (price: Price) => formatCurrencyWithoutZeroDecimals(price.value);

export const getMonthlyUnit = (price: Price, defaultPeriod: string) =>
    `${price.currency}/${price.period || defaultPeriod}`;

export const resolvePriceDisplayData = (
    prices: Prices,
    showMonthlyPrices: boolean,
    t: MessagesType,
): DisplayData | null => {
    // Priority follows requirements: 5G bundle, then plan bundle, standalone discount, no-discount pricing.
    return (
        resolveBundleLine({
            showMonthlyPrices,
            totalWithDiscount: prices.totalWith5gPlan,
            monthlyWithDiscount: prices.monthlyWith5gPlan,
            totalWithoutDiscount: prices.totalWithoutDiscount,
            monthlyWithoutDiscount: prices.monthlyWithoutDiscount,
            withDiscountLabel: t['eshop.product_page.product_offer_prices.with_5g_plan'],
            withoutDiscountLabel: t['eshop.product_page.product_offer_prices.without_5g_plan'],
        }) ||
        resolveBundleLine({
            showMonthlyPrices,
            totalWithDiscount: prices.totalWithPlan,
            monthlyWithDiscount: prices.monthlyWithPlan,
            totalWithoutDiscount: prices.totalWithoutDiscount,
            monthlyWithoutDiscount: prices.monthlyWithoutDiscount,
            withDiscountLabel: t['eshop.product_page.product_offer_prices.with_plan'],
            withoutDiscountLabel: t['eshop.product_page.product_offer_prices.without_plan'],
        }) ||
        resolveStandaloneLine({
            showMonthlyPrices,
            monthly: prices.monthlyWithDiscount,
            total: prices.totalWithDiscount,
            standard: prices.totalWithoutDiscount,
        }) ||
        resolveStandaloneLine({
            showMonthlyPrices,
            monthly: prices.monthlyWithoutDiscount,
            total: prices.totalWithoutDiscount,
        })
    );
};

// Monthly fields are required only when the UI variant displays monthly prices.
const hasRequiredMonthlyPrices = (showMonthlyPrices: boolean, ...required: (Price | undefined)[]) =>
    !showMonthlyPrices || required.every(isPriceSet);

type BundleLineInput = {
    showMonthlyPrices: boolean;
    totalWithDiscount?: Price;
    monthlyWithDiscount?: Price;
    totalWithoutDiscount?: Price;
    monthlyWithoutDiscount?: Price;
    withDiscountLabel: string;
    withoutDiscountLabel: string;
};

const resolveBundleLine = ({
    showMonthlyPrices,
    totalWithDiscount,
    monthlyWithDiscount,
    totalWithoutDiscount,
    monthlyWithoutDiscount,
    withDiscountLabel,
    withoutDiscountLabel,
}: BundleLineInput): DisplayData | null => {
    if (
        !isPriceSet(totalWithDiscount) ||
        !isPriceSet(totalWithoutDiscount) ||
        !hasRequiredMonthlyPrices(showMonthlyPrices, monthlyWithDiscount, monthlyWithoutDiscount)
    ) {
        return null;
    }

    return {
        primaryLine: {
            label: withDiscountLabel,
            monthlyPrice: showMonthlyPrices ? monthlyWithDiscount : undefined,
            showTooltip: true,
            totalPrice: totalWithDiscount,
        },
        secondaryLine: {
            label: withoutDiscountLabel,
            monthlyPrice: showMonthlyPrices ? monthlyWithoutDiscount : undefined,
            totalPrice: totalWithoutDiscount,
        },
    };
};

type StandaloneLineInput = {
    showMonthlyPrices: boolean;
    monthly?: Price;
    total?: Price;
    standard?: Price;
};

const resolveStandaloneLine = ({
    showMonthlyPrices,
    monthly,
    total,
    standard,
}: StandaloneLineInput): DisplayData | null => {
    if (!isPriceSet(total) || !hasRequiredMonthlyPrices(showMonthlyPrices, monthly)) {
        return null;
    }

    const hasDiscount = isPriceSet(standard) && standard.value > total.value;

    return {
        oldPrice: hasDiscount ? standard : undefined,
        primaryLine: {
            monthlyPrice: showMonthlyPrices ? monthly : undefined,
            totalPrice: total,
        },
    };
};
